/**
 * ReactionAndReplyPicker Component
 * Extended version of ReactionPicker with Reply button
 * iMessage-style emoji reaction picker + Reply action
 * Shows 7 emojis with horizontal scroll for more + Reply button below
 */

import { useEffect, useRef } from 'react'

interface ReactionAndReplyPickerProps {
  onEmojiSelect: (emoji: string) => void
  onReply: () => void // New: Reply button handler
  onClose: () => void
  position: 'above' | 'below'
  align?: 'left' | 'right' // Which side of message (for screen edge prevention)
  hasUserReaction?: boolean // Whether user already has a reaction on this message
  onRemoveReaction?: () => void // Callback to remove user's reaction
  hideReply?: boolean // Hide reply button for paid product messages
}

// Emoji set: 7 visible + 7 extended (14 total)
const QUICK_EMOJIS = ['❤️', '🦋', '😂', '👍', '🙏', '😍', '🔥', '😊']
const EXTENDED_EMOJIS = ['😢', '😮', '😡', '🤔', '💯', '✨', '🎉']
const ALL_EMOJIS = [...QUICK_EMOJIS, ...EXTENDED_EMOJIS]

export function ReactionAndReplyPicker({
  onEmojiSelect,
  onReply,
  onClose,
  position,
  align = 'left',
  hasUserReaction = false,
  onRemoveReaction,
  hideReply = false
}: ReactionAndReplyPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    // Add slight delay to prevent immediate close from the triggering click
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside as any)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside as any)
    }
  }, [onClose])

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const handleSelect = (emoji: string) => {
    onEmojiSelect(emoji)
    onClose()
  }

  const handleRemove = () => {
    if (onRemoveReaction) {
      onRemoveReaction()
      onClose()
    }
  }

  const handleReplyClick = () => {
    onReply()
    onClose()
  }

  return (
    <div
      ref={pickerRef}
      className={`absolute z-50 animate-fade-in ${
        position === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'
      } ${
        align === 'right' ? 'right-0' : 'left-0'
      }`}
    >
      <div className="bg-white rounded-lg shadow-xl border border-gray-200">
        {/* Emoji row */}
        <div className="flex gap-1 overflow-x-auto max-w-[280px] p-2 scroll-smooth">
          {ALL_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => handleSelect(emoji)}
              className="flex-shrink-0 text-2xl p-1 hover:scale-125 active:scale-110 transition-transform duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded"
              aria-label={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}

          {/* Cancel button - only show if user already has a reaction */}
          {hasUserReaction && onRemoveReaction && (
            <button
              onClick={handleRemove}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
              aria-label="Remove your reaction"
              title="Remove reaction"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <line x1="8" y1="8" x2="16" y2="16" strokeWidth="2" strokeLinecap="round" />
                <line x1="16" y1="8" x2="8" y2="16" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        {/* Divider and Reply button - hidden for paid product messages */}
        {!hideReply && (
          <>
            <div className="border-t border-gray-200" />
            <button
              onClick={handleReplyClick}
              className="w-full px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors duration-150 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
              aria-label="Reply to this message"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              Reply
            </button>
          </>
        )}
      </div>
    </div>
  )
}
